import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { defaultEmployeeNumberSettings } from '../fixtures/tenantSettingsFixtures';
import type { TenantSettingsPort } from '../port/TenantSettingsPort';
import { normalizeEmployeeNumberSettings, ruleKey } from './employeeNumber';
import type { AsyncResource } from '../../../shared/useAsyncResource';
import type { EmployeeNumberSettings } from './types';
import {
  createLazyResourceStore,
  createWorkspaceStateStore,
  useLazyResource,
  useWorkspaceState,
} from './workspaceStore';
export type TenantSettingsEmployeeNumberWorkspace = {
  resource: AsyncResource<EmployeeNumberSettings>;
  draft: EmployeeNumberSettings;
  baseline: EmployeeNumberSettings;
  dirty: boolean;
  valid: boolean;
  saving: boolean;
  error: string | undefined;
  notice: string | undefined;
  setDraft: Dispatch<SetStateAction<EmployeeNumberSettings>>;
  clearNotice: () => void;
  save: () => void;
  cancel: () => void;
  reset: () => void;
};
type EmployeeNumberWorkspaceState = Pick<
  TenantSettingsEmployeeNumberWorkspace,
  'draft' | 'baseline' | 'saving' | 'error' | 'notice'
>;
type EmployeeNumberWorkspaceStore = {
  resource: ReturnType<typeof createLazyResourceStore<EmployeeNumberSettings>>;
  state: ReturnType<typeof createWorkspaceStateStore<EmployeeNumberWorkspaceState>>;
  synchronizedData: EmployeeNumberSettings | undefined;
};
const employeeNumberWorkspaceStores = new WeakMap<TenantSettingsPort, EmployeeNumberWorkspaceStore>();
function employeeNumberWorkspaceStore(port: TenantSettingsPort) {
  let store = employeeNumberWorkspaceStores.get(port);
  if (!store) {
    store = {
      resource: createLazyResourceStore<EmployeeNumberSettings>(),
      state: createWorkspaceStateStore<EmployeeNumberWorkspaceState>({
        draft: defaultEmployeeNumberSettings,
        baseline: defaultEmployeeNumberSettings,
        saving: false,
        error: undefined,
        notice: undefined,
      }),
      synchronizedData: undefined,
    };
    employeeNumberWorkspaceStores.set(port, store);
  }
  return store;
}
export function useEmployeeNumberWorkspace(
  port: TenantSettingsPort,
  fixtureMode: boolean,
  canWrite: boolean,
): TenantSettingsEmployeeNumberWorkspace {
  const store = employeeNumberWorkspaceStore(port);
  const resource = useLazyResource(
    store.resource,
    () => (fixtureMode ? Promise.resolve(defaultEmployeeNumberSettings) : port.getEmployeeNumberSettings()),
    [fixtureMode, port],
  );
  const { draft, baseline, saving, error, notice } = useWorkspaceState(store.state);
  useEffect(() => {
    if (!resource.data || store.synchronizedData === resource.data) return;
    const normalized = normalizeEmployeeNumberSettings(resource.data);
    store.synchronizedData = resource.data;
    store.state.update((current) => ({
      ...current,
      draft: normalized,
      baseline: normalized,
    }));
  }, [resource.data, store]);
  const setDraft: Dispatch<SetStateAction<EmployeeNumberSettings>> = (update) =>
    store.state.update((current) => ({
      ...current,
      draft: typeof update === 'function' ? update(current.draft) : update,
    }));
  const dirty = ruleKey(draft) !== ruleKey(baseline);
  const valid =
    /^[A-Za-z0-9._-]{0,16}$/.test(draft.prefix) &&
    (draft.resetCycle === 'NEVER' ||
      (draft.resetCycle === 'YEARLY' && draft.dateFormat !== 'NONE') ||
      (draft.resetCycle === 'MONTHLY' && (draft.dateFormat === 'YYMM' || draft.dateFormat === 'YYYYMM')));
  const save = () => {
    if (!dirty || !valid || !canWrite || saving) return;
    store.state.update((current) => ({
      ...current,
      saving: true,
      error: undefined,
      notice: undefined,
    }));
    const request = fixtureMode
      ? Promise.resolve({
          ...draft,
          version: (baseline.version ?? 0) + 1,
          updatedAt: new Date().toISOString(),
        })
      : port.updateEmployeeNumberSettings(draft);
    void request
      .then(
        (settings) => {
          const normalized = normalizeEmployeeNumberSettings(settings);
          store.state.update((current) => ({
            ...current,
            draft: normalized,
            baseline: normalized,
            notice: '사번 발급 정책을 저장했습니다.',
          }));
        },
        (reason: unknown) => {
          store.state.update((current) => ({
            ...current,
            error: reason instanceof Error ? reason.message : '사번 발급 정책을 저장하지 못했습니다.',
          }));
        },
      )
      .finally(() =>
        store.state.update((current) => ({
          ...current,
          saving: false,
        })),
      );
  };
  return {
    resource,
    draft,
    baseline,
    dirty,
    valid,
    saving,
    error,
    notice,
    setDraft,
    clearNotice: () =>
      store.state.update((current) => ({
        ...current,
        notice: undefined,
      })),
    save,
    cancel: () => {
      if (!canWrite || saving) return;
      store.state.update((current) => ({
        ...current,
        draft: current.baseline,
        error: undefined,
        notice: undefined,
      }));
    },
    reset: () =>
      store.state.update((current) => ({
        ...current,
        draft: defaultEmployeeNumberSettings,
        notice: undefined,
      })),
  };
}
